<template>
  <div class="p-4 shadow flex flex-col justify-start gap-4 w-80">
    <div>
      <div class="flex justify-between items-center font-semibold mb-1">
        <div class="text-black">Technicians</div>
        <div class="flex justify-end text-gray-500 w-full">
          <Checkbox
            v-model="showAll"
            label="Include All Technicians"
            class="all-techs whitespace-nowrap mb-0"
          />
        </div>
      </div>
      <SelectInput
        :model-value="technicianId"
        :options="filteredTechOptions"
        placeholder="Auto Assign"
        :body-render="false"
        :filter="true"
        filter-placeholder="Search technicians..."
        :multiselect="isMultiTechEnabled"
        :max-selections="isMultiTechEnabled ? MAX_TECHS : undefined"
        :helper-text="limitHelperText"
        :max-height="150"
        @update:modelValue="update"
      />
    </div>
    <Button label="Save" class="w-full" @click="handleSave" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import Checkbox from "@/components/Checkbox.vue";
import Button from "@/components/Seranova/Buttons/Button.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import { useMultiTechSelect } from "@/composables/useMultiTechSelect";
import { fullName } from "@/mixins";
import { AppointmentV2 } from "@/typings/appointment";
import { Technician } from "@/typings/technician";

const emit = defineEmits(["saved"]);
const store = useStore();
const props = defineProps<{ appointment: AppointmentV2 }>();

const technicianId = ref<number | number[] | null>(null);
const showAll = ref(false);

const selectedTechIds = computed(() => {
  return Array.isArray(technicianId.value) ? technicianId.value : [];
});

const { isMultiTechEnabled, limitHelperText, MAX_TECHS } =
  useMultiTechSelect(selectedTechIds);

onMounted(() => {
  if (store.state.technicians.length === 0) {
    store.dispatch("fetchTechnicians");
  }
});

const techOptions = computed(() =>
  store.state.technicians.map((tech: Technician) => ({
    ...tech,
    label: fullName(tech),
    value: tech.technician_profile_id
  }))
);

const serviceCategoryId = computed(
  () => props.appointment.service_category?.id ?? null
);

const filteredTechOptions = computed(() => {
  if (!serviceCategoryId.value || showAll.value) {
    return techOptions.value;
  }

  const currentIds = Array.isArray(technicianId.value)
    ? technicianId.value
    : technicianId.value
    ? [technicianId.value]
    : [];

  return techOptions.value.filter(
    (tech) =>
      (tech.service_categories_ids || []).includes(serviceCategoryId.value!) ||
      currentIds.includes(tech.technician_profile_id)
  );
});

function getInitialTechIds(): number | number[] | null {
  if (isMultiTechEnabled.value) {
    const { appointment_technicians } = props.appointment;
    if (appointment_technicians?.length) {
      return [...appointment_technicians]
        .sort((a, b) => a.position - b.position)
        .map((at) => at.technician_profile_id);
    }
    // Wrap scalar tech ID in array for multiselect mode to prevent
    // SelectInput from clearing the existing assignment.
    const techId = props.appointment?.technician?.technician_id;
    return techId ? [techId] : [];
  }
  return props.appointment?.technician?.technician_id ?? null;
}

technicianId.value = getInitialTechIds();

watch(
  () => [
    props.appointment.technician_profile_id,
    props.appointment.appointment_technicians
  ],
  () => {
    technicianId.value = getInitialTechIds();
  }
);

const update = (val: number | number[] | null) => {
  if (Array.isArray(val) && val.length > MAX_TECHS) return;
  technicianId.value = val;
};

const handleSave = async () => {
  const payload: Record<string, any> = {
    appointmentId: props.appointment.id
  };

  if (isMultiTechEnabled.value && Array.isArray(technicianId.value)) {
    payload.technician_profile_ids = technicianId.value;
  } else {
    payload.technician_profile_id = technicianId.value;
  }

  try {
    await store.dispatch("assignAppointmentEvent", payload);
    window.notify.success("Appointment technician updated");
    emit("saved");
    tippy.hideAll();
  } catch {
    technicianId.value = getInitialTechIds();
  }
};
</script>

<style lang="scss" scoped>
.all-techs {
  font-size: 0.9em;
}
</style>
